import { useRef } from "react";
import { useMutation, type DefaultError, type QueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { capturePrivateSession, assertPrivateSessionLifetime, getPrivateSessionState } from "@/lib/api/private-session";

/** Identity controls may advance their own generation once, but never a sibling's. */
export function useSessionMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>, client?: QueryClient,
) {
  const current = useRef(capturePrivateSession());
  const lifetime = current.current;
  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: options.mutationFn ? (...args) => {
      assertPrivateSessionLifetime(lifetime);
      return options.mutationFn!(...args);
    } : undefined,
  }, client);
  return {
    ...mutation,
    mutateAsync: async (...args: Parameters<typeof mutation.mutateAsync>) => {
      assertPrivateSessionLifetime(lifetime);
      try {
        const result = await mutation.mutateAsync(...args);
        if (getPrivateSessionState().generation !== lifetime.generation + 1) assertPrivateSessionLifetime(lifetime);
        return result;
      } finally {
        if (getPrivateSessionState().generation === lifetime.generation + 1) current.current = capturePrivateSession();
      }
    },
  };
}
